import { UnifiedAudioEngine } from './UnifiedAudioEngine';
import { RecordedAudioEngine } from './RecordedAudioEngine';
import { IAudioEngine, AudioState, AudioConfig } from './IAudioEngine';
import { supabase } from '@/integrations/supabase/client';
import { GeneratedContentService } from '@/services/generatedContentService';
import { logger } from '@/utils/logger';
import { audioMetadataCache } from './AudioMetadataCache';
import { audioPositionTracker } from './AudioPositionTracker';

export interface GlobalAudioState extends AudioState {
  moduleId?: string;
  courseId?: string;
  moduleTitle?: string;
  courseTitle?: string;
  volume: number;
  rate: number;
  pitch: number;
  source: 'recorded' | 'tts';
  audioUrl?: string;
}

class GlobalAudioServiceClass {
  private engine: IAudioEngine | null = null;
  private abortController: AbortController | null = null;
  private listeners: Set<(state: GlobalAudioState) => void> = new Set();
  private config: AudioConfig = { volume: 0.8, rate: 1, pitch: 1 };
  private currentState: GlobalAudioState = {
    isPlaying: false,
    isPaused: false,
    isStopped: true,
    currentTime: 0,
    duration: 0,
    progress: 0,
    currentWordIndex: 0,
    error: null,
    volume: 0.8,
    rate: 1,
    pitch: 1,
    source: 'recorded',
  };

  /**
   * PHASE 3: Get audio URL synchronously from cache (for instant playback)
   */
  getAudioUrlSync(moduleId: string, courseId: string): string | null {
    try {
      // Use static import with defensive checks
      const cached = audioMetadataCache.get(moduleId, courseId);
      return cached?.audioUrl || null;
    } catch (error) {
      console.warn('[GlobalAudioService] Cache lookup failed (non-critical):', error);
      return null;
    }
  }

  /**
   * Initialize from database audio (preferred)
   * PHASE 2: Optimized for instant playback when cached + comprehensive logging
   */
  async initializeFromDbAudio(options: {
    text: string;
    moduleId: string;
    courseId: string;
    moduleTitle?: string;
    courseTitle?: string;
  }): Promise<void> {
    const initId = `${options.moduleId}_${Date.now()}`;
    console.log(`🎧 [GlobalAudioService ${initId}] Starting audio initialization:`, {
      moduleId: options.moduleId,
      courseId: options.courseId,
      moduleTitle: options.moduleTitle
    });
    
    // Store context
    this.currentState.moduleId = options.moduleId;
    this.currentState.courseId = options.courseId;
    this.currentState.moduleTitle = options.moduleTitle;
    this.currentState.courseTitle = options.courseTitle;

    try {
      // PHASE 2: Synchronous cache check for instant playback
      const cachedAudio = audioMetadataCache.get(options.moduleId, options.courseId);
      
      console.log(`🔍 [GlobalAudioService ${initId}] Cache check:`, {
        found: !!cachedAudio,
        hasUrl: !!cachedAudio?.audioUrl,
        audioUrl: cachedAudio?.audioUrl
      });

      let audio = cachedAudio;
      
      // If cached, initialize engine immediately (synchronous path)
      if (cachedAudio && cachedAudio.audioUrl) {
        console.log(`⚡ [GlobalAudioService ${initId}] Cache HIT - instant audio initialization!`);
        
        this.currentState.source = 'recorded';
        this.currentState.audioUrl = cachedAudio.audioUrl;
        this.currentState.error = null; // PHASE 2: Clear any previous errors
        
        // Destroy old engine if exists
        if (this.engine && this.engine.destroy) {
          this.engine.destroy();
        }
        
        this.engine = new RecordedAudioEngine();
        this.engine.onStateChange((state) => {
          this.currentState = {
            ...this.currentState,
            ...state,
            volume: this.config.volume,
            rate: this.config.rate,
            pitch: this.config.pitch,
            source: 'recorded',
            audioUrl: cachedAudio.audioUrl,
          };
          this.notifyListeners();
        });

        // PHASE 4: Pass pre-fetched URL for instant initialization
        await (this.engine as RecordedAudioEngine).initializeWithUrl(
          cachedAudio.audioUrl,
          options.moduleId,
          options.courseId,
          this.config
        );
        
        console.log(`✅ [GlobalAudioService ${initId}] Audio ready from cache`);
        
        // Notify immediately - audio is ready
        this.notifyListeners();
        return;
      }
      
      // Only fetch from DB if not in cache (rare case)
      if (!cachedAudio) {
        console.log(`⏳ [GlobalAudioService ${initId}] Cache miss - fetching from DB with retry...`);
        
        audio = await GeneratedContentService.getModuleAudio(
          options.moduleId,
          options.courseId
        );

        console.log(`🔍 [GlobalAudioService ${initId}] DB fetch result:`, {
          hasAudio: !!audio,
          hasUrl: !!(audio as any)?.audio_url || !!audio?.audioUrl,
          audioUrl: (audio as any)?.audio_url || audio?.audioUrl
        });
      }

      if (audio && (audio.audioUrl || (audio as any).audio_url)) {
        const audioUrl = audio.audioUrl || (audio as any).audio_url;
        
        console.log(`✅ [GlobalAudioService ${initId}] Using professional recorded audio from DB`);
        
        this.currentState.source = 'recorded';
        this.currentState.audioUrl = audioUrl;
        this.currentState.error = null; // PHASE 2: Clear any previous errors
        
        // Destroy old engine if exists
        if (this.engine && this.engine.destroy) {
          this.engine.destroy();
        }
        
        this.engine = new RecordedAudioEngine();
        this.engine.onStateChange((state) => {
          this.currentState = {
            ...this.currentState,
            ...state,
            volume: this.config.volume,
            rate: this.config.rate,
            pitch: this.config.pitch,
            source: 'recorded',
            audioUrl: audioUrl,
          };
          this.notifyListeners();
        });

        await (this.engine as RecordedAudioEngine).initialize(
          audioUrl,
          options.moduleId,
          options.courseId,
          this.config
        );
        
        console.log(`✅ [GlobalAudioService ${initId}] Audio engine initialized successfully`);
      } else {
        // NO TTS FALLBACK - Set error state
        console.warn(`⚠️ [GlobalAudioService ${initId}] No DB audio available`);
        
        this.currentState.error = 'No audio available';
        this.currentState.source = 'recorded';
      }
    } catch (err) {
      console.error(`❌ [GlobalAudioService ${initId}] Error loading DB audio:`, {
        error: err instanceof Error ? err.message : err,
        stack: err instanceof Error ? err.stack : undefined,
        moduleId: options.moduleId,
        courseId: options.courseId,
      });
      
      this.currentState.error = 'Failed to load audio';
    }

    this.notifyListeners();
  }

  /**
   * @deprecated TTS is no longer supported - use initializeFromDbAudio only
   */
  initializeFromText(options: {
    text: string;
    moduleId: string;
    courseId: string;
    moduleTitle?: string;
    courseTitle?: string;
  }) {
    console.warn('⚠️ initializeFromText is deprecated – DB audio only');
  }

  /**
   * @deprecated Legacy method - use initializeFromDbAudio instead
   */
  initialize(text: string, moduleId: string, courseId: string, moduleTitle?: string, courseTitle?: string) {
    console.warn('⚠️ initialize(text, ...) is deprecated – use initializeFromDbAudio');
  }

  getEngine(): IAudioEngine | null {
    return this.engine;
  }

  getState(): GlobalAudioState {
    return { ...this.currentState };
  }

  isReady(): boolean {
    return this.engine !== null;
  }

  subscribe(listener: (state: GlobalAudioState) => void) {
    this.listeners.add(listener);
    listener(this.currentState);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentState));
  }

  async play() {
    if (!this.engine) {
      console.warn('Audio engine not initialized');
      return;
    }
    try {
      await this.engine.play();
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  }

  pause() {
    if (!this.engine) {
      console.warn('Audio engine not initialized');
      return;
    }
    try {
      this.engine.pause();
      
      // Save current position when paused (for music player resume feature)
      if (this.currentState.moduleId && this.currentState.courseId && this.currentState.currentTime > 0) {
        audioPositionTracker.savePosition(
          this.currentState.moduleId,
          this.currentState.courseId,
          this.currentState.currentTime,
          this.currentState.duration
        );
      }
    } catch (error) {
      console.error('Failed to pause audio:', error);
    }
  }

  stop() {
    if (!this.engine) {
      console.warn('Audio engine not initialized');
      return;
    }
    try {
      this.engine.stop();
      
      // PHASE 2: Clear all module context to reset player state
      this.currentState.moduleId = undefined;
      this.currentState.courseId = undefined;
      this.currentState.moduleTitle = undefined;
      this.currentState.courseTitle = undefined;
      this.currentState.audioUrl = undefined;
      
      // Notify listeners of cleared state
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to stop audio:', error);
    }
  }

  seekTo(time: number) {
    if (!this.engine) {
      console.warn('Audio engine not initialized');
      return;
    }
    try {
      this.engine.seekTo(time);
    } catch (error) {
      console.error('Failed to seek audio:', error);
    }
  }

  skipForward(seconds: number = 30) {
    if (this.engine) {
      const newTime = Math.min(this.currentState.currentTime + seconds, this.currentState.duration);
      this.engine.seekTo(newTime);
    }
  }

  skipBackward(seconds: number = 30) {
    if (this.engine) {
      const newTime = Math.max(this.currentState.currentTime - seconds, 0);
      this.engine.seekTo(newTime);
    }
  }

  setVolume(volume: number) {
    this.config.volume = volume;
    this.currentState.volume = volume;
    if (this.engine) {
      try {
        this.engine.setVolume(volume);
      } catch (error) {
        console.error('Failed to set volume:', error);
      }
    }
    this.notifyListeners();
  }

  setRate(rate: number) {
    this.config.rate = rate;
    this.currentState.rate = rate;
    if (this.engine) {
      try {
        this.engine.setRate(rate);
      } catch (error) {
        console.error('Failed to set rate:', error);
      }
    }
    this.notifyListeners();
  }

  destroy() {
    if (this.engine) {
      this.engine.stop();
      if (this.engine.destroy) {
        this.engine.destroy();
      }
      this.engine = null;
    }

    // PHASE 8: Clear engine pools to prevent memory leak
    (this as any).ttsEnginePool = null;
    (this as any).recordedEnginePool = null;

    this.listeners.clear();
    
    // PHASE 8: Cleanup audio prefetch service
    import('./AudioPrefetchService').then(({ audioPrefetchService }) => {
      audioPrefetchService.cleanup();
    });
  }

  /**
   * Cancel any pending audio operations (Phase 3)
   */
  cancelPendingOperations(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      console.log('🚫 Cancelled pending audio operations');
    }
  }

  /**
   * Cleanup audio resources (Phase 3)
   */
  cleanup(): void {
    this.cancelPendingOperations();
    if (this.engine) {
      this.engine.stop();
    }
  }
}

export const GlobalAudioService = new GlobalAudioServiceClass();
